// Silver Sage — Executive Office error UX helper.
// See mem/preferences/errors-are-for-people.md for the governing rule.
//
// Every failure surface must answer four questions in plain English:
//   1. What happened?    (human subject, no codes)
//   2. What does it mean? (operational impact — optional if obvious)
//   3. Did anything change? (reassurance about state)
//   4. What should I do next? (one recommendation)
//
// Raw technical strings (error.message, HTTP codes, SQL, UUIDs, edge function
// failures) are NEVER surfaced above the fold. They are tucked into the toast's
// description behind "Show Technical Details" — the description remains
// collapsed until the Director opens it.

import { toast } from "sonner";
import { TECHNICAL_DETAILS_EVENT } from "@/components/system/TechnicalDetailsDialog";

/**
 * Strip anything credential-shaped before technical details are shown or
 * logged: signed-storage tokens, api keys, bearer tokens, JWTs.
 */
function redact(input: string): string {
  return input
    .replace(/([?&](?:token|apikey|api_key|access_token|signature|sig)=)[^&\s"']+/gi, "$1[redacted]")
    .replace(/(bearer\s+)[A-Za-z0-9._~+/-]+=*/gi, "$1[redacted]")
    .replace(/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, "[redacted-token]");
}

export interface FriendlyErrorOptions {
  /** Plain-English headline. e.g. "I couldn't save Holly's payment." */
  what: string;
  /** Optional operational meaning. e.g. "Her payment hasn't been recorded yet." */
  meaning?: string;
  /** Reassurance. Defaults to "Nothing has changed." */
  changed?: string;
  /** Single recommended next step. e.g. "Try again in a moment." */
  nextStep?: string;
  /** Raw error — will be captured for Show Technical Details, never displayed above the fold. */
  technical?: unknown;
  /** Which operation failed, e.g. "Upload image to storage". Shown in technical details. */
  operation?: string;
}

function extractTechnical(err: unknown): string | undefined {
  if (!err) return undefined;
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object") {
    const anyErr = err as {
      message?: unknown;
      error?: unknown;
      code?: unknown;
      details?: unknown;
      hint?: unknown;
      status?: unknown;
      statusCode?: unknown;
      name?: unknown;
    };
    const fields = [
      ["Code", anyErr.code],
      ["Message", anyErr.message ?? anyErr.error],
      ["Details", anyErr.details],
      ["Hint", anyErr.hint],
      ["HTTP status", anyErr.status ?? anyErr.statusCode],
      ["Error type", anyErr.name],
    ]
      .filter((entry): entry is [string, string | number] =>
        typeof entry[1] === "string" || typeof entry[1] === "number",
      )
      .map(([label, value]) => `${label}: ${String(value)}`);
    if (fields.length) return fields.join("\n");
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}

/**
 * Show a human-first error toast. Technical details are logged to the console
 * for developers and offered behind a "Show technical details" action button
 * — never rendered above the fold.
 */
export function friendlyError(opts: FriendlyErrorOptions): void {
  const {
    what,
    meaning,
    changed = "Nothing has changed.",
    nextStep = "Try again in a moment. If it keeps happening, I'll explain what's going on.",
    technical,
    operation,
  } = opts;

  const description = [meaning, changed, nextStep].filter(Boolean).join(" ");
  const techString = extractTechnical(technical);

  const toastId = `friendly-error-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  // Details are offered whenever we know *anything* — which stage failed counts.
  // A silent, detail-free failure used to render the action button as a no-op.
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const fullDetails =
    techString || operation
      ? redact(
          [
            operation ? `Operation: ${operation}` : null,
            techString ?? "No further detail was returned by the server.",
            offline ? "Browser reported: no network connection at the time of the failure." : null,
            `When: ${new Date().toISOString()}`,
          ]
            .filter(Boolean)
            .join("\n"),
        )
      : undefined;

  if (fullDetails) {
    // Developer signal — never shown above the fold.
    // eslint-disable-next-line no-console
    console.error("[SilverSage]", what, "—", fullDetails);
  }

  toast.error(what, {
    id: toastId,
    description,
    duration: 8000,
    action: fullDetails
      ? {
          label: "Show technical details",
          onClick: () => {
            toast.dismiss(toastId);
            window.dispatchEvent(
              new CustomEvent(TECHNICAL_DETAILS_EVENT, {
                detail: { what, details: fullDetails },
              }),
            );
          },
        }
      : undefined,
  });

}

/**
 * Quiet, professional success toast. No confetti, no celebration.
 * "✓ Holly's payment has been recorded."
 */
export function friendlySuccess(message: string, detail?: string): void {
  toast.success(message, detail ? { description: detail } : undefined);
}
